import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Download,
    Search,
    Wrench,
    Gauge,
    Link2,
    Award,
    Share2,
    CheckCircle2,
} from "lucide-react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/primitives";
import { CTABand } from "../components/CTABand";

const AT_A_GLANCE = [
    ["Name", "Global Green Hotel (GGH)"],
    ["Type", "Sustainability initiative and hospitality platform"],
    ["Sector", "Hotels, tourism and hospitality"],
    ["Initial Focus", "Food waste and sustainable hotel operations"],
    ["Long-Term Focus", "A broader global framework for responsible and regenerative hospitality"],
    ["Core Belief", "Better sustainability can create better hospitality and stronger business"],
];

const WHAT_GGH_DOES = [
    { icon: Search, title: "Assess", desc: "Identify sustainability opportunities and operational gaps." },
    { icon: Wrench, title: "Improve", desc: "Help hotels develop practical actions that fit their operations." },
    { icon: Gauge, title: "Measure", desc: "Encourage simple, meaningful indicators of progress and value." },
    { icon: Link2, title: "Connect", desc: "Bring hospitality businesses, experts, communities, researchers, and partners together." },
    { icon: Award, title: "Recognize", desc: "Highlight hotels and teams that demonstrate genuine progress." },
    { icon: Share2, title: "Share", desc: "Publish ideas, case studies, research, tools, and lessons that others can use." },
];

const VALUE_CYCLE = [
    ["Measure", "Understand current performance."],
    ["Improve", "Turn findings into practical action."],
    ["Save", "Reduce waste and improve efficiency."],
    ["Verify", "Build confidence in reported progress."],
    ["Recognize", "Celebrate meaningful achievement."],
    ["Promote", "Help responsible hotels tell their story."],
    ["Benchmark", "Learn from progress across the industry."],
];

const AIMS = [
    "Reduce waste and unnecessary resource use.",
    "Improve hotel operational efficiency and financial performance.",
    "Encourage responsible practices across hotel departments.",
    "Recognize and share genuine sustainability progress.",
    "Connect hotels with useful knowledge, partners, research, and opportunities.",
    "Help travelers better understand and support responsible hospitality.",
];

const DIFFERENT = [
    ["Practical", "We focus on actions hotels can realistically implement."],
    ["Business-minded", "We connect sustainability with efficiency, resilience and financial value."],
    ["Evidence-led", "We encourage measurement, learning and transparent progress."],
    ["Collaborative", "We bring hotels, communities, experts and partners into the same conversation."],
    ["Globally scalable", "The framework is designed to begin locally and grow across regions and countries."],
];

const SectionTitle = ({ children }) => (
    <h2 className="font-display mt-16 text-2xl font-medium tracking-tight text-[#0A1711] sm:text-3xl">
        {children}
    </h2>
);

const Body = ({ children }) => (
    <p className="mt-5 text-base leading-relaxed text-[#4A5D54] md:text-lg">{children}</p>
);

const ArticleProfile = () => (
    <main>
        <SEO
            title="GGH Organization Profile | Global Green Hotel"
            description="The complete introduction to Global Green Hotel: what GGH is, why it exists, how it creates value, and what makes it different."
        />
        <PageHero
            testid="article-profile-hero"
            eyebrow="Research & Insights — Featured Publication"
            titleLines={["Global Green Hotel:", "Organization Profile"]}
            description="What GGH is, why it exists, how it creates value, and what makes it different — the complete introduction to the initiative."
        />

        <article data-testid="article-profile" className="py-20 sm:py-28">
            <div className="mx-auto max-w-[880px] px-5 sm:px-8">
                <Reveal>
                    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E8E6E1] bg-white p-6">
                        <p className="text-sm text-[#4A5D54]">
                            <span className="font-bold text-[#0A1711]">Publication:</span> GGH
                            Organization Profile · Global Green Hotel
                        </p>
                        <a
                            href="/assets/ggh-organization-profile.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="article-download-pdf"
                            className="btn-green !px-5 !py-2.5"
                        >
                            <Download size={15} aria-hidden="true" />
                            Download PDF
                        </a>
                    </div>
                </Reveal>

                <Reveal>
                    <p className="font-display mt-12 border-l-2 border-[#C2A160] pl-6 text-xl italic leading-relaxed text-[#1A3B2B] md:text-2xl">
                        Global Green Hotel (GGH) is a sustainability initiative and hospitality
                        platform created to help hotels turn sustainability into practical action,
                        measurable value, and a stronger guest experience.
                    </p>
                </Reveal>

                <Reveal>
                    <SectionTitle>1. What Is GGH?</SectionTitle>
                    <Body>
                        Global Green Hotel (GGH) is a hospitality-focused sustainability initiative
                        that connects hotels, hospitality professionals, communities, travelers,
                        researchers, and partners around one simple idea: hotels can protect the
                        environment while becoming stronger and more efficient businesses.
                    </Body>
                    <Body>
                        GGH is designed to help hotels understand where sustainability
                        opportunities exist, take practical action, measure progress, learn from
                        one another, and communicate their achievements with credibility.
                    </Body>
                </Reveal>

                <Reveal>
                    <SectionTitle>2. Why GGH?</SectionTitle>
                    <Body>
                        The hospitality industry depends on clean water, energy, food, healthy
                        communities, natural resources, and attractive destinations. At the same
                        time, hotels face rising operating costs, resource waste, changing guest
                        expectations, and growing pressure to operate responsibly.
                    </Body>
                    <Body>
                        GGH exists to bridge that gap. We believe sustainability should not be
                        treated only as a cost or a marketing message. When done well, it can
                        reduce waste, improve efficiency, strengthen communities, support
                        destinations, and create long-term business value.
                    </Body>
                </Reveal>

                <Reveal>
                    <SectionTitle>3. GGH at a Glance</SectionTitle>
                    <dl className="mt-7 overflow-hidden rounded-2xl border border-[#E8E6E1]" data-testid="article-at-a-glance">
                        {AT_A_GLANCE.map(([k, v], i) => (
                            <div
                                key={k}
                                className={`grid gap-1 px-6 py-4 sm:grid-cols-3 sm:gap-4 ${i % 2 ? "bg-white" : "bg-[#F2EFE7]"}`}
                            >
                                <dt className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#A4803F]">{k}</dt>
                                <dd className="text-sm font-semibold text-[#0A1711] sm:col-span-2">{v}</dd>
                            </div>
                        ))}
                    </dl>
                </Reveal>

                <Reveal>
                    <SectionTitle>4. Vision & Mission</SectionTitle>
                    <blockquote className="mt-7 rounded-2xl border-l-2 border-[#C2A160] bg-white p-7">
                        <p className="font-display text-xl italic leading-relaxed text-[#1A3B2B]">
                            “A greener, stronger, and more responsible global hospitality industry.”
                        </p>
                    </blockquote>
                    <Body>
                        Our mission is to help hotels adopt practical, measurable, and financially
                        responsible sustainability practices that benefit the environment, people,
                        communities, guests, and the hotel business.
                    </Body>
                </Reveal>

                <Reveal>
                    <SectionTitle>5. Our Aim</SectionTitle>
                    <Body>
                        GGH aims to make sustainability easier for hotels to understand, implement,
                        measure, improve, and communicate.
                    </Body>
                    <ul className="mt-6 grid gap-3" data-testid="article-aims">
                        {AIMS.map((a) => (
                            <li key={a} className="flex items-start gap-3 rounded-xl border border-[#E8E6E1] bg-white p-4">
                                <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#1A3B2B]" aria-hidden="true" />
                                <span className="text-sm font-semibold text-[#0A1711]">{a}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal>
                    <SectionTitle>6. What GGH Does</SectionTitle>
                    <div className="mt-7 grid gap-4 sm:grid-cols-2" data-testid="article-what-ggh-does">
                        {WHAT_GGH_DOES.map(({ icon: Icon, title, desc }) => (
                            <div key={title} className="card-lift rounded-2xl border border-[#E8E6E1] bg-white p-6">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1A3B2B] text-[#D9B96A]">
                                    <Icon size={18} aria-hidden="true" />
                                </span>
                                <h3 className="font-display mt-5 text-xl font-medium uppercase tracking-wide text-[#0A1711]">
                                    {title}
                                </h3>
                                <p className="mt-2 text-sm leading-relaxed text-[#4A5D54]">{desc}</p>
                            </div>
                        ))}
                    </div>
                </Reveal>

                <Reveal>
                    <SectionTitle>7. The GGH Sustainability Framework</SectionTitle>
                    <Body>
                        GGH's framework spans six connected pillars: Environment (energy, water,
                        waste, emissions and natural-resource protection), Food (food-waste
                        prevention, responsible purchasing, recovery and circular practices),
                        People (staff participation, learning, inclusion, wellbeing and responsible
                        workplace practices), Community (local partnerships, local value creation
                        and destination stewardship), Business (efficiency, cost savings,
                        innovation, resilience and long-term value) and Governance (clear goals,
                        accountability, transparency, measurement and continuous improvement).
                    </Body>
                    <Link to="/framework" data-testid="article-framework-link" className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#1A3B2B] transition-colors hover:text-[#A4803F]">
                        Explore the full framework
                        <ArrowUpRight size={15} aria-hidden="true" />
                    </Link>
                </Reveal>

                <Reveal>
                    <SectionTitle>8. How GGH Creates Value</SectionTitle>
                    <Body>
                        GGH is built around a practical cycle: understand the current situation,
                        identify opportunities, take action, measure progress, learn, and improve.
                    </Body>
                    <ol className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="article-value-cycle">
                        {VALUE_CYCLE.map(([step, desc], i) => (
                            <li key={step} className="flex items-center gap-4 rounded-xl border border-[#E8E6E1] bg-white p-4">
                                <span className="font-display text-2xl font-medium text-[#1A3B2B]/20">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span>
                                    <span className="block text-sm font-extrabold uppercase tracking-wider text-[#0A1711]">{step}</span>
                                    <span className="block text-xs text-[#4A5D54]">{desc}</span>
                                </span>
                            </li>
                        ))}
                    </ol>
                </Reveal>

                <Reveal>
                    <SectionTitle>9. Who GGH Serves</SectionTitle>
                    <Body>
                        GGH is intended to serve hotels and resorts of different sizes, hospitality
                        professionals, tourism organizations, communities, travelers, researchers,
                        sustainability experts, educational institutions, and responsible business
                        partners.
                    </Body>
                </Reveal>

                <Reveal>
                    <SectionTitle>10. Our Initial Focus</SectionTitle>
                    <Body>
                        GGH will begin with practical sustainability challenges that hotels can
                        act on and measure. Food waste is an important starting point because it
                        connects sustainability, kitchen operations, purchasing, cost control,
                        staff practices, guest experience, and resource efficiency.
                    </Body>
                    <Body>
                        As GGH develops, the framework can expand to energy, water, waste,
                        biodiversity, procurement, community impact, climate resilience, and other
                        areas of responsible and regenerative hospitality.
                    </Body>
                </Reveal>

                <Reveal>
                    <SectionTitle>11. What Makes GGH Different</SectionTitle>
                    <ul className="mt-7 grid gap-3" data-testid="article-different">
                        {DIFFERENT.map(([k, v]) => (
                            <li key={k} className="rounded-xl border border-[#E8E6E1] bg-white p-5">
                                <span className="text-sm font-extrabold uppercase tracking-wider text-[#A4803F]">{k}</span>
                                <span className="mt-1 block text-sm leading-relaxed text-[#0A1711]">{v}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>

                <Reveal>
                    <div className="grain relative mt-16 overflow-hidden rounded-2xl bg-[#04150D] p-9 sm:p-12">
                        <div className="relative z-[2]">
                            <Eyebrow dark>GGH Principle</Eyebrow>
                            <p className="font-display mt-5 text-2xl font-medium leading-snug text-[#FDFBF7] sm:text-3xl">
                                “Sustainability should protect the planet, strengthen people and
                                communities, and make good business sense.”
                            </p>
                        </div>
                    </div>
                </Reveal>
            </div>
        </article>

        <CTABand
            testid="cta-band-article"
            heading="Want to Be Part of What GGH Is Building?"
            text="Hotels, researchers, communities and partners are welcome to start a conversation."
            primary={{ to: "/get-involved", label: "Get Involved" }}
            secondary={{ to: "/contact", label: "Contact GGH" }}
        />
    </main>
);

export default ArticleProfile;
